import type { Reference } from '@medplum/fhirtypes'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import { getReferenceString } from '@seen/core/references'
import { extractId, extractVersion } from '@seen/core/utils'
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseBulkReadVersionsOptions<T extends ExtendedResource> {
  query?: Omit<UseQueryOptions<T[], Error, T[]>, 'queryKey' | 'queryFn'>
}

export function useBulkReadVersions<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  references: Reference[],
  options: UseBulkReadVersionsOptions<T> = {}
): UseQueryResult<T[]> {
  const medplum = useMedplum()
  const resourceType = resourceClass.RESOURCE_TYPE

  const referencesKey = references.map(getReferenceString).join(',')
  const validReferences = references.flatMap((reference) => {
    const id = extractId(reference)
    const versionId = extractVersion(reference)
    return id && versionId ? [{ id, versionId }] : []
  })

  const { enabled, ...queryOptions } = options.query ?? {}

  const queryResult = useQuery({
    ...queryOptions,
    enabled: validReferences.length > 0 && enabled !== false,

    queryKey: cacheKeys.bulkReadVersions(
      resourceType,
      referencesKey,
      resourceClass.name
    ),

    queryFn: async (): Promise<T[]> => {
      const promises = validReferences.map(({ id, versionId }) =>
        medplum
          .readVersionClass(resourceClass, id, versionId)
          .catch((error) => {
            console.error(
              `Error reading ${resourceType}/${id}/_history/${versionId}:`,
              error
            )
            return null
          })
      )

      const results = await Promise.all(promises)

      return results.filter((result) => result !== null)
    },
  })

  useErrorToast(
    queryResult.error &&
      `Error bulk reading ${resourceType} versions: ${queryResult.error.message ?? 'Unknown error'}`
  )

  return queryResult
}
